export type GalleryItem = {
  url: string;
  caption?: string;
};

export type GalleryConfig = {
  /** Number of images per row in the grid. Defaults to 3 when missing or invalid. */
  columnCount?: number;
  items: GalleryItem[];
};

export type GalleryEditRequest = {
  initialConfig: GalleryConfig;
  onCommit: (config: GalleryConfig) => void | Promise<void>;
};

export const DEFAULT_GALLERY_COLUMN_COUNT = 3;
export const MAX_GALLERY_COLUMN_COUNT = 12;

function getObjectValue(
  input: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined {
  const value = input[key];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function getStringValue(
  input: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
}

function normalizeGalleryColumnCount(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return DEFAULT_GALLERY_COLUMN_COUNT;
  }

  const n = Math.floor(raw);
  if (n < 1) return DEFAULT_GALLERY_COLUMN_COUNT;

  if (n > MAX_GALLERY_COLUMN_COUNT) return MAX_GALLERY_COLUMN_COUNT;

  return n;
}

function normalizeGalleryItem(raw: unknown): GalleryItem | null {
  if (typeof raw === 'string') {
    const url = raw.trim();
    return url ? { url } : null;
  }

  if (!raw || typeof raw !== 'object') return null;

  const rec = raw as Record<string, unknown>;
  const file = getObjectValue(rec, 'file');
  const image = getObjectValue(rec, 'image');
  const url =
    getStringValue(rec, ['url', 'src']) ??
    (file ? getStringValue(file, ['url', 'src']) : undefined) ??
    (image ? getStringValue(image, ['url', 'src']) : undefined);
  if (!url) return null;

  const caption = getStringValue(rec, ['caption', 'title', 'alt']);
  const item: GalleryItem = { url };
  if (caption) {
    item.caption = caption;
  }

  return item;
}

export function normalizeGalleryConfig(input: unknown): GalleryConfig {
  if (!input || typeof input !== 'object') {
    return { columnCount: DEFAULT_GALLERY_COLUMN_COUNT, items: [] };
  }

  const rec = input as Record<string, unknown>;
  const nested = getObjectValue(rec, 'data') ?? getObjectValue(rec, 'config');
  const source = nested ?? rec;
  const itemsRaw = source.items;
  const items: GalleryItem[] = [];
  if (Array.isArray(itemsRaw)) {
    for (const el of itemsRaw) {
      const item = normalizeGalleryItem(el);
      if (item) items.push(item);
    }
  }

  return {
    columnCount: normalizeGalleryColumnCount(
      source.columnCount ?? source.columns,
    ),
    items,
  };
}

export function stringifyGalleryConfig(config: GalleryConfig): string {
  return JSON.stringify(normalizeGalleryConfig(config), null, 2);
}
